import { PreviewOutlined } from "@mui/icons-material";
import { Stack, Tabs, Typography } from "@mui/material";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import {
  WorkOrdersPermission,
  WorkOrdersPoProcessPermission,
} from "commons/enum/permission";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EditButton,
  GoBackIconButton,
  RemoveButton,
} from "share-components/mui-wrap/button/action-button";
import { HoverMenuButton } from "share-components/mui-wrap/button/hover-menu-button";
import { XTab } from "share-components/mui-wrap/tab/x-tab";
import { PageContentScrollableContainer } from "share-components/templates/page/page-content-container";
import { PageDetailContainer } from "share-components/templates/page/page-detail-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { EditWorkOrderPo } from "../edit-work-order-po/edit-work-order-po";
import { WorkOrderPoStatusManagement } from "../work-order-po-status-management/work-order-po-status-management";
import { PrintOnBackPreview } from "./prints/print-on-back-preview";
import { PrintOnFrontPreview } from "./prints/print-on-front-preview";

import { useCheckPermission } from "commons/hooks/use-check-permission";
import { XButton } from "share-components/mui-wrap/button/x-button";
import { WorkOrderInfo } from "./work-order-info";
import { WorkOrderProcess } from "./work-order-process/work-order-process";

type WorkOrderTab = "workOrderInfo" | "workOrderProgress";

export const WorkOrderPoDetail = observer(() => {
  const { workOrderId } = useParams();
  const {
    workOrderPoManagement: {
      getWorkOrderPoDetail,
      openEdit,
      openUpdateStatus,
      workOrderPoPrintingManagement,
    },
  } = useStores();
  const [tab, setTab] = useState<WorkOrderTab>("workOrderInfo");
  const allowAccessWorkOrdersPoProcess = useCheckPermission([
    WorkOrdersPoProcessPermission.Default,
  ]);
  useEffect(() => {
    if (workOrderId) {
      getWorkOrderPoDetail(workOrderId);
    }
  }, [getWorkOrderPoDetail, workOrderId]);
  return (
    <PageDetailContainer>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
        }}
      >
        <XTab label="Chi tiết lệnh sản xuất" value={"workOrderInfo"} />
        <XTab
          label="Theo dõi tiến độ sản xuất"
          value={"workOrderProgress"}
          disabled={!allowAccessWorkOrdersPoProcess.allowAccess}
        />
      </Tabs>
      <PageContentScrollableContainer>
        {tab === "workOrderInfo" && <WorkOrderInfo></WorkOrderInfo>}
        {tab === "workOrderProgress" && <WorkOrderProcess></WorkOrderProcess>}
      </PageContentScrollableContainer>

      <>
        {openEdit && <EditWorkOrderPo></EditWorkOrderPo>}
        {openUpdateStatus && (
          <WorkOrderPoStatusManagement></WorkOrderPoStatusManagement>
        )}
        {workOrderPoPrintingManagement.openPrintOnFrontPreview && (
          <PrintOnFrontPreview></PrintOnFrontPreview>
        )}
        {workOrderPoPrintingManagement.openPrintOnBackPreview && (
          <PrintOnBackPreview></PrintOnBackPreview>
        )}
      </>
    </PageDetailContainer>
  );
});
type HeaderProps = {};
const Header = observer((props: HeaderProps) => {
  const {
    workOrderPoManagement: {
      workOrderPoDetail,
      setProp: setWorkOrderPoManagementProp,
      workOrderPoPrintingManagement,
    },
    deleteConfirmationStore: { setProp, setDeletionCallback },
  } = useStores();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onDelete = useCallback(
    async (id: string) => {
      if (id) {
        await workOrderPoBusiness.delete(id);
        goBack();
      }
    },
    [goBack],
  );
  const removeHandler = useCallback(() => {
    setProp("open", true);
    setDeletionCallback(() => {
      if (!workOrderPoDetail?.id) return;
      onDelete(workOrderPoDetail?.id);
    });
  }, [workOrderPoDetail, onDelete, setDeletionCallback, setProp]);

  const editHandler = useCallback(() => {
    setWorkOrderPoManagementProp("openEdit", true);
  }, [setWorkOrderPoManagementProp]);

  const openPrintOnFrontPreviewHandler = useCallback(() => {
    workOrderPoPrintingManagement.setProp("openPrintOnFrontPreview", true);
  }, [workOrderPoPrintingManagement]);

  const openPrintOnBackPreviewHandler = useCallback(() => {
    workOrderPoPrintingManagement.setProp("openPrintOnBackPreview", true);
  }, [workOrderPoPrintingManagement]);

  const onUpdateOrderStatusHandler = useCallback(() => {
    setWorkOrderPoManagementProp("openUpdateStatus", true);
  }, [setWorkOrderPoManagementProp]);

  return (
    <>
      <Stack
        direction={"row"}
        justifyItems={"center"}
        spacing={1}
        alignItems={"center"}
      >
        <GoBackIconButton onClick={goBack}></GoBackIconButton>
        <Typography>
          {`Thông tin chi tiết: `}{" "}
          <span style={{ color: "blue", fontWeight: "bold", marginRight: "25px" }}>
            {[
              workOrderPoDetail?.customerDisplay?.dealingName,
              workOrderPoDetail?.product?.name,
              workOrderPoDetail?.woNumber,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </Typography>
      </Stack>
      <Stack direction={"row"} justifyItems={"center"} spacing={1}>
        <HoverMenuButton
          label="In LSX"
          buttonProps={{
            variant: "outlined",
            startIcon: <PreviewOutlined />,
          }}
          options={[
            {
              id: "T1",
              label: "Mẫu 1: LSX Trước In",
              onClick: openPrintOnFrontPreviewHandler,
            },
            {
              id: "T2",
              label: "Mẫu 2: LSX Sau In",
              onClick: openPrintOnBackPreviewHandler,
            },
          ]}
        ></HoverMenuButton>

        <XButton
          size="small"
          accessPermission={[WorkOrdersPermission.Update]}
          variant="contained"
          onClick={onUpdateOrderStatusHandler}
          sx={{ width: "140px" }}
        >
          Cập nhật trạng thái
        </XButton>

        <RemoveButton
          accessPermission={[WorkOrdersPermission.Delete]}
          onClick={removeHandler}
        ></RemoveButton>

        <EditButton
          accessPermission={[WorkOrdersPermission.Update]}
          onClick={editHandler}
        ></EditButton>
      </Stack>
    </>
  );
});
